import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import fetchAllianceData from '~api/fetchAllianceData';
import Button from '~components/Button/Button';
import ConditionalWrapper from '~components/ConditionalWrapper/ConditionalWrapper';
import { getColor, getUrlParam, setCookie } from '~globals/common';
import constants from '~globals/constants';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';
import PhoneIcon from '~images/inline/phone.inline.svg';
import { updateCallService } from '~redux/Actions/GlobalActions';

export const getPartnerAllianceInfo = async (
    dispatch: ReturnType<typeof useDispatch>,
    allid: string
) => {
    if (!allid) {
        console.error('[getPartnerAllianceInfo] No AllianceId found');
        return { error: 'No AllianceId found' };
    }
    try {
        const res: AllianceDataProps = await fetchAllianceData({
            queryKey: ['allianceInfo', allid],
        });
        if (!res.data) {
            throw res.errors;
        }
        if (res.data.partnerAllianceInfo) {
            const {
                suppressEmailCapturePopup,
                allianceData,
                lightModeHeader,
                partnershipType,
                segmentType,
            } = res.data.partnerAllianceInfo;
            const { partnerDisplayName, serviceHours, throttle, allianceInfo } = allianceData;
            const { allianceLogo, allianceCompanyUrl, tcpaDisclaimer } = allianceInfo ?? '';
            setCookie('ehi.alliance_id', allid)
            dispatch(
                updateCallService({
                    allianceId: allid,
                    companyUrl: allianceCompanyUrl,
                    hours: serviceHours ? serviceHours.replaceAll(',', ':') : '',
                    lightModeHeader,
                    logo: allianceLogo,
                    partnerName: partnerDisplayName !== 'false' ? partnerDisplayName : '',
                    partnershipType: partnershipType,
                    segmentType: segmentType,
                    suppressEmailCapturePopup,
                    tcpaDisclaimer,
                    throttle: getUrlParam('defaultThrottle') || throttle,
                })
            );
        }
    } catch (err) {
        console.error('[getPartnerAllianceInfo]', err);
    }
};

type PhoneProps = {
    islink?: boolean;
    isbtn?: 'primary' | 'secondary' | 'reskin' | 'orange' | 'link' | 'rebrandAction';
    classname?: string;
    icon?: string;
    format?: 'default' | 'modified';
    hidedefaultchildren?: boolean;
    fromComponentName?: string;
};

export const Phone = ({
    islink = false,
    isbtn,
    classname = '',
    children,
    icon,
    format = 'default',
    hidedefaultchildren = false,
    fromComponentName = 'Phone Button',
}: React.PropsWithChildren<PhoneProps>) => {
    const stateNumber = useAppSelector((state) => state.global.phone.number);
    const statePhoneLoaded = useAppSelector((state) => state.global.phone.isFinal);
    const [phoneNumber, setPhoneNumber] = useState(constants.DEFAULT_PHONE.toString());
    const [phoneLoaded, setPhoneLoaded] = useState(false);

    let formattedNumber: string;

    formattedNumber = phoneNumber;
    if (format === 'modified') {
        const parts: string[] = formattedNumber.split('-');
        formattedNumber = `(${parts[0]}) ${parts[1]}-${parts[2]}`;
    } else {
        formattedNumber = `1-${formattedNumber}`;
    }

    useEffect(() => {
        if (stateNumber) {
            setPhoneNumber(stateNumber);
        }
    }, [stateNumber]);

    useEffect(() => {
        if (statePhoneLoaded) {
            setPhoneLoaded(statePhoneLoaded);
        }
    }, [statePhoneLoaded]);

    const phoneButtonClicked = () => {
        fireTrackEvent({ page: { action: `${fromComponentName}: call us` } });
    };

    return isbtn ? (
        <Button
            styletype={isbtn}
            islink={true}
            forceATag={true}
            href={`tel:${phoneNumber?.replaceAll(/\D/g, '')}`}
            className={classname}
            onClick={phoneButtonClicked}
        >
            {!hidedefaultchildren && icon && (
                <PhoneIcon fill={getColor(icon)} className="mr-3 inline" />
            )}
            {!hidedefaultchildren && formattedNumber}
            {hidedefaultchildren && children}
        </Button>
    ) : (
        <span
            className={classNames(
                islink && !classname.includes('text-white') && 'text-primary-dark',
                classname
            )}
        >
            <ConditionalWrapper
                condition={islink}
                wrapper={(children) => (
                    <a
                        href={`tel:${phoneNumber?.replaceAll(/\D/g, '')}`}
                        aria-label="phone number"
                        className="inline-flex items-center"
                        onClick={phoneButtonClicked}
                    >
                        {children}
                    </a>
                )}
            >
                {!hidedefaultchildren && icon && (
                    <PhoneIcon fill={getColor(icon)} className="mr-2 inline" />
                )}
                {!hidedefaultchildren && (
                    <span
                        className="phone-number duration-500 ease-in-out opacity-0 transition-opacity data-[loaded=true]:opacity-100"
                        data-loaded={phoneLoaded}
                    >
                        {formattedNumber}
                    </span>
                )}
                {!hidedefaultchildren && children && <span className="ml-1">{children}</span>}
                {hidedefaultchildren && children}
            </ConditionalWrapper>
        </span>
    );
};

type HoursProps = { isinline?: boolean; classname?: string };

export const Hours = ({ isinline = false, classname }: HoursProps) => {
    const isPhoneFinal = useAppSelector((state) => state.global.phone.isFinal);
    const hours = useAppSelector((state) => state.global.hours);

    const [shouldDisplayHours, setShouldDisplayHours] = useState(isPhoneFinal || !isinline);

    // hide hours until phone number is updated by invoca to disguise the number swap in header
    useEffect(() => {
        if (isPhoneFinal) setShouldDisplayHours(true);
    }, [isPhoneFinal]);

    useEffect(() => {
        setTimeout(() => {
            setShouldDisplayHours(true);
        }, 3000);
    }, []);

    if (shouldDisplayHours) {
        return (
            <span className={classname}>
                {isinline ? (
                    hours.replace(' <br/>', ', ')
                ) : (
                    <span dangerouslySetInnerHTML={{ __html: hours }}></span>
                )}
            </span>
        );
    }
    return null;
};

export const PartnerLogo = ({
    className,
    logoshadow,
}: {
    className?: string;
    logoshadow?: boolean;
}) => {
    const allid = useAppSelector((state) => state.global.allianceId);
    const logo = useAppSelector((state) => state.global.logo);
    const companyUrl = useAppSelector((state) => state.global.companyUrl);
    const waLink: string = process.env.GATSBY_ANALYTICS_SITE_GROUP + ':header:partner_logo';

    return logo ? (
        <div
            id="partner-logo"
            className={classNames(
                'bg-white items-center h-12',
                className,
                logoshadow && 'logo-shadow',
                allid === constants.DEFAULT_ALLIANCE_ID ? 'hidden' : 'flex'
            )}
        >
            <ConditionalWrapper
                condition={!!companyUrl}
                wrapper={(children) => (
                    <a id="partner-logo-link" data-wa-link={waLink} href={companyUrl}>
                        {children}
                    </a>
                )}
            >
                <div className="w-28 h-8">
                    <img
                        id="partner-logo-img"
                        className="mx-auto max-h-8"
                        alt="partner logo"
                        src={logo}
                    />
                </div>
            </ConditionalWrapper>
        </div>
    ) : null;
};

export const isAdvocateAllId = () => {
    const allid = useAppSelector((state) => state.global.allianceId);
    const advocateAllIds = [
        'adv7833000',
        'adv7834000',
        'adv7835000',
        'adv7940000',
        'adv7832000',
        'adv9160000',
        'adv9152000',
        'adv7941000',
        'adv9154000',
        'adv9153000',
        'adv7863000',
        'adv10353000',
        'adv10354000',
        'adv10355000',
        'adv9161000',
    ];

    return advocateAllIds.some((advAllId) => advAllId === allid);
};
